import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

// Context for sidebar state
type SidebarContextType = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextType | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

// SidebarProvider
type SidebarProviderProps = React.HTMLAttributes<HTMLDivElement>;

const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  ({ className, children, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(true);
    const [openMobile, _setOpenMobile] = React.useState(false);
    const isMobile = useIsMobile();

    const openMobileTimeoutRef = React.useRef<number | null>(null);
    const lastMobileCloseAtRef = React.useRef<number>(0);

    const setOpenMobile = React.useCallback((open: boolean) => {
      const minReopenDelayMs = 120;

      if (openMobileTimeoutRef.current !== null) {
        window.clearTimeout(openMobileTimeoutRef.current);
        openMobileTimeoutRef.current = null;
      }

      if (open) {
        const elapsed = Date.now() - lastMobileCloseAtRef.current;
        if (elapsed < minReopenDelayMs) {
          openMobileTimeoutRef.current = window.setTimeout(() => {
            _setOpenMobile(true);
            openMobileTimeoutRef.current = null;
          }, minReopenDelayMs - elapsed);
          return;
        }

        _setOpenMobile(true);
        return;
      }

      lastMobileCloseAtRef.current = Date.now();
      _setOpenMobile(false);
    }, []);

    React.useEffect(() => {
      return () => {
        if (openMobileTimeoutRef.current !== null) {
          window.clearTimeout(openMobileTimeoutRef.current);
          openMobileTimeoutRef.current = null;
        }
      };
    }, []);

    const toggleSidebar = React.useCallback(() => {
      if (isMobile) {
        setOpenMobile(!openMobile);
      } else {
        setIsOpen((prev) => !prev);
      }
    }, [isMobile, openMobile, setOpenMobile]);

    const contextValue = React.useMemo(
      () => ({
        isOpen,
        setIsOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [isOpen, isMobile, openMobile, toggleSidebar]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <div ref={ref} className={cn('relative min-h-screen', className)} {...props}>
          {children}
        </div>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = 'SidebarProvider';

// SidebarCollapseButton - unique gradient button for collapse
type SidebarCollapseButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  isCollapsed?: boolean;
};

const SidebarCollapseButton = React.forwardRef<HTMLButtonElement, SidebarCollapseButtonProps>(
  ({ className, isCollapsed, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-collapsed={isCollapsed}
      className={cn('btn-sidebar-collapse', className)}
      {...props}
    >
      <span className="material-symbols-outlined icon" style={{ fontSize: '20px' }}>
        {isCollapsed ? 'chevron_right' : 'chevron_left'}
      </span>
    </button>
  )
);
SidebarCollapseButton.displayName = 'SidebarCollapseButton';

// Sidebar - transparent wrapper with inner bordered container
type SidebarProps = React.HTMLAttributes<HTMLDivElement>;

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(({ className, children, ...props }, ref) => {
  const { isOpen, setIsOpen, isMobile, openMobile, setOpenMobile } = useSidebar();

  const borderOverlayStyle = React.useMemo<React.CSSProperties>(
    () => ({
      maskComposite: 'exclude',
      WebkitMaskComposite: 'xor',
    }),
    []
  );

  const sidebarTransitionMs = React.useMemo(() => {
    const fallbackMs = 300;
    if (typeof window === 'undefined') return fallbackMs;

    const raw = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--sidebar-transition-duration')
      .trim();

    if (!raw) return fallbackMs;

    if (raw.endsWith('ms')) {
      const value = Number(raw.replace('ms', '').trim());
      return Number.isFinite(value) ? value : fallbackMs;
    }

    if (raw.endsWith('s')) {
      const value = Number(raw.replace('s', '').trim());
      return Number.isFinite(value) ? value * 1000 : fallbackMs;
    }

    const value = Number(raw);
    return Number.isFinite(value) ? value : fallbackMs;
  }, []);

  // Apply collapsed-only layout (centering, etc.) after the width transition finishes.
  const [collapsedLayoutEnabled, setCollapsedLayoutEnabled] = React.useState(!isOpen);

  React.useEffect(() => {
    if (isOpen) {
      setCollapsedLayoutEnabled(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCollapsedLayoutEnabled(true);
    }, sidebarTransitionMs);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, sidebarTransitionMs]);

  // Keep hook order stable across mobile/desktop renders.
  React.useEffect(() => {
    if (!isMobile || !openMobile) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMobile(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, openMobile, setOpenMobile]);

  // Mobile sheet state - 'closed', 'open', or 'closing' for CSS transitions
  // We always keep the mobile sidebar mounted (but hidden) to avoid first-open lag on real devices
  const mobileSheetState = React.useMemo<'closed' | 'open'>(() => {
    if (!isMobile) return 'closed';
    return openMobile ? 'open' : 'closed';
  }, [isMobile, openMobile]);

  // Desktop sidebar with new design
  if (!isMobile) {
    return (
      <aside ref={ref} className={cn('sidebar-shell', !isOpen && 'sidebar-shell--collapsed', className)} {...props}>
        {/* Transparent parent (padded) */}
        <div className="sidebar-shell__inner">
          {/* Collapse button is a sibling of the bordered container */}
          <div className="sidebar-shell__collapse">
            <SidebarCollapseButton isCollapsed={!isOpen} onClick={() => setIsOpen(!isOpen)} />
          </div>

          {/* Bordered child that respects parent padding */}
          <div className={cn('sidebar-container', collapsedLayoutEnabled && 'sidebar-container--collapsed')}>
            <div aria-hidden className="sidebar-container__border" style={borderOverlayStyle} />
            {children}
          </div>
        </div>
      </aside>
    );
  }

  // Mobile sidebar (Sheet-like)
  // Always mounted but hidden when closed for smooth animations on first open
  const mobileSheet = (
    <div className="sidebar-mobile-overlay" data-state={mobileSheetState}>
      {/* Backdrop */}
      <div className="sidebar-mobile-backdrop" onClick={() => setOpenMobile(false)} />

      {/* Sheet panel */}
      <aside ref={ref} role="dialog" aria-modal="true" className={cn('sidebar-mobile-panel', className)} {...props}>
        <div className="sidebar-shell__inner">
          <div className="sidebar-container">
            <div aria-hidden className="sidebar-container__border" style={borderOverlayStyle} />
            {children}
          </div>
        </div>
      </aside>
    </div>
  );

  return typeof document === 'undefined' ? null : createPortal(mobileSheet, document.body);
});
Sidebar.displayName = 'Sidebar';

// SidebarTrigger
type SidebarTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(({ className, ...props }, ref) => {
  const { isOpen, setIsOpen } = useSidebar();

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className={cn(
        'inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-slate-100',
        className
      )}
      {...props}
    >
      <span className="material-symbols-outlined">menu</span>
      <span className="sr-only">Toggle sidebar</span>
    </button>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

// SidebarHeader
type SidebarHeaderProps = React.HTMLAttributes<HTMLDivElement>;

const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex-shrink-0', className)} {...props} />
));
SidebarHeader.displayName = 'SidebarHeader';

// SidebarContent
type SidebarContentProps = React.HTMLAttributes<HTMLDivElement>;

const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex-1 overflow-y-auto', className)} {...props} />
));
SidebarContent.displayName = 'SidebarContent';

// SidebarFooter
type SidebarFooterProps = React.HTMLAttributes<HTMLDivElement>;

const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex-shrink-0', className)} {...props} />
));
SidebarFooter.displayName = 'SidebarFooter';

// SidebarGroup
type SidebarGroupProps = React.HTMLAttributes<HTMLDivElement>;

const SidebarGroup = React.forwardRef<HTMLDivElement, SidebarGroupProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-2', className)} {...props} />
));
SidebarGroup.displayName = 'SidebarGroup';

// SidebarGroupContent
type SidebarGroupContentProps = React.HTMLAttributes<HTMLDivElement>;

const SidebarGroupContent = React.forwardRef<HTMLDivElement, SidebarGroupContentProps>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('space-y-1', className)} {...props} />
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

// SidebarMenu
type SidebarMenuProps = React.HTMLAttributes<HTMLUListElement>;

const SidebarMenu = React.forwardRef<HTMLUListElement, SidebarMenuProps>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn('space-y-1', className)} {...props} />
));
SidebarMenu.displayName = 'SidebarMenu';

// SidebarMenuItem
type SidebarMenuItemProps = React.HTMLAttributes<HTMLLIElement>;

const SidebarMenuItem = React.forwardRef<HTMLLIElement, SidebarMenuItemProps>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

// SidebarMenuButton
type SidebarMenuButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  isActive?: boolean;
};

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ className, isActive, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-active={isActive}
      className={cn(
        // Base navigation button styles from CSS
        'btn-nav',
        // Selected/active state
        isActive && 'btn-nav--selected',
        className
      )}
      {...props}
    />
  )
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
  SidebarCollapseButton,
};
